import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TenantAuthService } from 'src/app/services/tenant-auth.service';

export interface TaxRegisterBlock {
  fiscalNumber: string;
  blockedBy?: string;
  blockedAt: string;
  // Shown to the user with the standard text.
  message?: string;
}

// Blocks per company: { [tenantId]: { [fiscalNumber]: block } }.
const BLOCKS_KEY = 'pos_tax_register_blocks';

// Thrown by every operation of a blocked register (TaxService._runRegisterExclusive).
export class TaxRegisterBlockedError extends Error {
  constructor(readonly block: TaxRegisterBlock) {
    super(taxRegisterBlockText(block));
    this.name = 'TaxRegisterBlockedError';
  }
}

export function taxRegisterBlockText(block: TaxRegisterBlock): string {
  const base = `Служба підтримки працює з вашим ПРРО ${block.fiscalNumber}. Фіскальні операції тимчасово недоступні.`;
  return block.message ? `${base} ${block.message}` : base;
}

// An admin's block of a tax register: the register stays on (not disabled) and keeps its state,
// but every fiscal operation, upload and context change made on the terminal is refused until
// the admin lifts it. Kept in localStorage per company, so it is checked without async storage
// and survives restarts.
@Injectable({ providedIn: 'root' })
export class TaxRegisterBlockService {
  private readonly _blocks = new BehaviorSubject<TaxRegisterBlock[]>([]);
  // The current company's blocked registers.
  readonly blocks$ = this._blocks.asObservable();

  constructor(private _tenantAuth: TenantAuthService) {
    this._blocks.next(this.list());
  }

  list(): TaxRegisterBlock[] {
    return Object.values(this._read()[this._tenant()] || {});
  }

  get(fiscalNumber: string): TaxRegisterBlock | null {
    return this._read()[this._tenant()]?.[this._id(fiscalNumber)] || null;
  }

  // Throws TaxRegisterBlockedError while the register is blocked.
  assertNotBlocked(fiscalNumber: string): void {
    const block = this.get(fiscalNumber);
    if (block) {
      throw new TaxRegisterBlockedError(block);
    }
  }

  block(fiscalNumber: string, blockedBy?: string, message?: string): TaxRegisterBlock {
    const id = this._id(fiscalNumber);
    if (!id) {
      throw new Error('fiscalNumber is required');
    }

    const block: TaxRegisterBlock = {
      fiscalNumber: id,
      blockedBy: blockedBy || undefined,
      blockedAt: new Date().toISOString(),
      message: message?.trim() || undefined,
    };
    this._update((blocks) => ({ ...blocks, [id]: block }));
    return block;
  }

  unblock(fiscalNumber: string): void {
    const id = this._id(fiscalNumber);
    this._update((blocks) => {
      const { [id]: _, ...rest } = blocks;
      return rest;
    });
  }

  // Again after the company changed.
  refresh(): void {
    this._blocks.next(this.list());
  }

  private _update(change: (blocks: Record<string, TaxRegisterBlock>) => Record<string, TaxRegisterBlock>): void {
    const tenant = this._tenant();
    if (!tenant) {
      throw new Error('No company is selected on this terminal');
    }

    const all = this._read();
    all[tenant] = change(all[tenant] || {});
    try {
      localStorage.setItem(BLOCKS_KEY, JSON.stringify(all));
    } catch {
      throw new Error('The block can not be saved: no local storage');
    }
    this._blocks.next(this.list());
  }

  private _read(): Record<string, Record<string, TaxRegisterBlock>> {
    try {
      const value = JSON.parse(localStorage.getItem(BLOCKS_KEY) || '{}');
      return value && typeof value === 'object' ? value : {};
    } catch {
      return {};
    }
  }

  private _tenant(): string {
    try {
      return this._tenantAuth.tenantId || '';
    } catch {
      return '';
    }
  }

  private _id(fiscalNumber: string): string {
    return fiscalNumber == null ? '' : String(fiscalNumber).trim();
  }
}
